import { Colors, Lightning } from '@lightningjs/sdk';
import { getImageTextureObj } from 'support/generalUtils';
import VisibilityIndicator from 'components/common/VisibilityIndicator';
import { CtvCarouselItem } from 'types/api/config';
import { colors } from '../../../../aliases';
import TextLabel from 'components/common/TextLabel';

export interface LoginLandingBillboardTemplateSpec
  extends Lightning.Component.TemplateSpec {
  mediaItem: CtvCarouselItem | null;
  carouselIndex: number | undefined;
  Background: {
    BackgroundContain: Lightning.textures.ImageTexture;
  };
  GradientPrimary: Lightning.Component;
  GradientSecondary: Lightning.Component;
  Content: {
    Title: typeof TextLabel;
    Subtitle: typeof TextLabel;
  };
  VisibilityIndicator: typeof VisibilityIndicator;
}

const CAROUSEL_X = 1030;
const CAROUSEL_Y = 889;
const CONTENT_WIDTH = 800;
const TITLE_FONT_SIZE = 36;
const SUB_TITLE_FONT_SIZE = 22;
const TITLE_MARGIN = 4;
const SUB_TITLE_MARGIN = 1;

const BILLBOARD_WIDTH = 1920;
const BILLBOARD_HEIGHT = 1080;

const GRADIENT_WIDTH = BILLBOARD_WIDTH;
const GRADIENT_PRIMARY_HEIGHT = BILLBOARD_HEIGHT * 0.35; // Gradient starts 35% up
const GRADIENT_SECONDARY_HEIGHT = 176;
const GRADIENT_PRIMARY_Y = BILLBOARD_HEIGHT - GRADIENT_PRIMARY_HEIGHT;
const GRADIENT_SECONDARY_Y = BILLBOARD_HEIGHT - GRADIENT_SECONDARY_HEIGHT;

export default class LoginLandingBillboard
  extends Lightning.Component<LoginLandingBillboardTemplateSpec>
  implements
    Lightning.Component
      .ImplementTemplateSpec<LoginLandingBillboardTemplateSpec>
{
  private _mediaItem: CtvCarouselItem | null = null;
  private _backgroundImageUri = '';
  private _notifyWhenFullyVisible = false;

  private _Background = this.getByRef('Background')!;
  private _GradientPrimary = this.getByRef('GradientPrimary')!;
  private _GradientSecondary = this.getByRef('GradientSecondary')!;
  private _BackgroundContain = this._Background.getByRef('BackgroundContain')!;
  private _Content = this.getByRef('Content')!;
  private _Title = this._Content.getByRef('Title')!;
  private _Subtitle = this._Content.getByRef('Subtitle')!;
  protected _VisibilityIndicator = this.getByRef('VisibilityIndicator')!;

  carouselIndex: number | undefined;

  static get width() {
    return BILLBOARD_WIDTH;
  }

  static get height() {
    return BILLBOARD_HEIGHT;
  }

  get mediaItem() {
    return this._mediaItem;
  }

  set mediaItem(mediaItem: CtvCarouselItem | null) {
    this._mediaItem = mediaItem;
    if (!mediaItem) return;

    this.updateTitle(mediaItem.title);
    this.updateSubtitle(mediaItem.subtitle);
    this.updateBackground(mediaItem.image_url);

    this._VisibilityIndicator.patch({
      visible: this._notifyWhenFullyVisible && !!this._mediaItem,
    });
  }

  get notifyWhenFullyVisible() {
    return this._notifyWhenFullyVisible;
  }

  set notifyWhenFullyVisible(notifyWhenFullyVisible: boolean) {
    this._notifyWhenFullyVisible = notifyWhenFullyVisible;

    this._VisibilityIndicator.patch({
      visible: this._notifyWhenFullyVisible && !!this._mediaItem,
    });
  }

  static override _template(): Lightning.Component.Template<LoginLandingBillboardTemplateSpec> {
    return {
      Background: {
        w: BILLBOARD_WIDTH,
        h: BILLBOARD_HEIGHT,
        BackgroundContain: {},
      },
      GradientPrimary: {
        y: GRADIENT_PRIMARY_Y,
        w: GRADIENT_WIDTH,
        h: GRADIENT_PRIMARY_HEIGHT,
        rect: true,
        colorTop: Colors('appBackground').alpha(0).get(),
        colorBottom: Colors('appBackground').get(),
      },
      GradientSecondary: {
        y: GRADIENT_SECONDARY_Y,
        w: GRADIENT_WIDTH,
        h: GRADIENT_SECONDARY_HEIGHT,
        rect: true,
        colorTop: Colors('appBackground').alpha(0).get(),
        colorBottom: Colors('appBackground').get(),
      },
      Content: {
        x: CAROUSEL_X,
        y: CAROUSEL_Y,
        w: CONTENT_WIDTH,
        flex: { direction: 'column', alignItems: 'flex-end' },
        Title: {
          type: TextLabel,
          flexItem: {
            marginTop: TITLE_MARGIN,
            marginBottom: TITLE_MARGIN,
          },
          text: {
            fontSize: TITLE_FONT_SIZE,
            textColor: Colors('labelText').get(),
            fontStyle: 'bold',
            advancedRenderer: true,
          },
        },
        Subtitle: {
          type: TextLabel,
          flexItem: {
            marginTop: SUB_TITLE_MARGIN,
            marginBottom: SUB_TITLE_MARGIN,
          },
          text: {
            fontSize: SUB_TITLE_FONT_SIZE,
            textColor: Colors('labelText').get(),
          },
        },
      },
      VisibilityIndicator: {
        type: VisibilityIndicator,
        w: BILLBOARD_WIDTH,
        h: BILLBOARD_HEIGHT,
        signals: { indicateFullyVisible: '$indicateFullyVisible' },
        visible: false,
      },
    };
  }

  private updateTitle(title: string) {
    const accentRegex = /<accent>(.*?)<\/accent>/g;
    let formattedText = title;

    if (accentRegex.test(title)) {
      const accentColor: string = colors.appAccent.replace('#', '');
      formattedText = title.replace(accentRegex, (match, accentText) => {
        return `<color=0xff${accentColor}>${accentText}</color>`;
      });
    }

    this._Title.patch({ text: { text: formattedText } });
  }

  private updateSubtitle(subtitle: string) {
    this._Subtitle.patch({ text: { text: subtitle } });
  }

  private updateBackground(imageUrl: string) {
    this._backgroundImageUri = imageUrl;

    this._BackgroundContain.patch(
      getImageTextureObj(
        this._backgroundImageUri,
        BILLBOARD_WIDTH,
        BILLBOARD_HEIGHT,
      ),
    );
    this._GradientPrimary.patch({ w: BILLBOARD_WIDTH });
    this._GradientSecondary.patch({ w: BILLBOARD_WIDTH });
  }

  private $indicateFullyVisible() {
    this.handleVisibilitySignal();
  }

  protected handleVisibilitySignal() {
    if (this._notifyWhenFullyVisible && this._mediaItem) {
      this.fireAncestors(
        '$onBillboardFullyVisible',
        this._mediaItem,
        this.carouselIndex ?? 0,
      );
    }
  }

  $billboardSelected() {
    if (!this._mediaItem) return;
    this.fireAncestors(
      '$onBillboardSelect',
      this._mediaItem,
      false,
      this._backgroundImageUri,
      this.carouselIndex,
    );
  }
}
